/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-lone-blocks */
import SimpleSelect from "component/Common/simpleSelect";
import React, { Fragment, useMemo, useState } from "react";
import { Card, Col, Collapse, Image } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Nouislider from "nouislider-react";
import "nouislider/distribute/nouislider.css";
import { useFlightContext } from "hooks/flight/useFlightContext";
import useApiClient from "hooks/useApiClient";
import { FaAngleDown, FaAngleUp } from "react-icons/fa6";
import { getMinMaxPrice } from "helpers/flightHelper";

const SideBar = ({ filters, setFilters, allFlights }) => {
    const { t } = useTranslation();
    const { urls } = useApiClient();
    const [isOpen, setIsOpen] = useState(false);
    const [activeTimeTab, setActiveTimeTab] = useState(0);
    const { availabilityResults } = useFlightContext();
    const { airlineDictionary, airportDictionary } = availabilityResults || {
        airlineDictionary: {},
        airportDictionary: {},
    };
    const airports = airportDictionary || {};

    // Helper function to get flight stops
    const getFlightStops = (flight, directionOrIndex) => {
        if (typeof directionOrIndex === 'number') {
            if (flight.bounds && flight.bounds[directionOrIndex] && flight.bounds[directionOrIndex].segments) {
                return flight.bounds[directionOrIndex].segments.length - 1;
            }
            return 0;
        }

        if (flight.bounds && flight.bounds[0] && flight.bounds[0].segments) {
            if (directionOrIndex === 'departure') {
                return flight.bounds[0].segments.length - 1;
            } else if (directionOrIndex === 'return' && flight.bounds[1] && flight.bounds[1].segments) {
                return flight.bounds[1].segments.length - 1;
            }
            return flight.bounds[0].segments.length - 1;
        }
        return 0;
    };

    // Price lives at flight.recommendation.price.detailed_price.total
    const getPrice = (flight) => {
        return flight?.recommendation?.price?.detailed_price?.total ?? null;
    };

    const getTripTypeFromFlight = (flight) => {
        const bounds = flight?.bounds;
        if (!Array.isArray(bounds)) return 'ow';

        if (bounds.length === 2) {
            const dep0 = bounds?.[0]?.departure?.locationId;
            const arr0 = bounds?.[0]?.arrival?.locationId;
            const dep1 = bounds?.[1]?.departure?.locationId;
            const arr1 = bounds?.[1]?.arrival?.locationId;

            const isRt = dep0 != null && arr0 != null && dep1 != null && arr1 != null && dep0 === arr1 && arr0 === dep1;
            return isRt ? 'rt' : 'md';
        }

        return bounds.length > 1 ? 'md' : 'ow';
    };

    // Calculate best price per stop
    const getBestPricePerStop = () => {
        if (!allFlights || allFlights.length === 0) {
            return {};
        }

        const best = {};
        const tripType = getTripTypeFromFlight(allFlights[0]);
        const isRoundTrip = tripType === 'rt';
        const isMultiDest = tripType === 'md';

        const update = (key, price) => {
            if (best[key] == null || price < best[key]) best[key] = price;
        };

        if (isRoundTrip) {
            allFlights.forEach(flight => {
                const depStops = getFlightStops(flight, 'departure');
                const retStops = getFlightStops(flight, 'return');
                const price = getPrice(flight);
                if (price === null) return;

                update(`dep-${depStops}`, price);
                update(`ret-${retStops}`, price);
                update(`${depStops}-${retStops}`, price);
            });

        } else if (isMultiDest) {
            // MD
            allFlights.forEach(flight => {
                const price = getPrice(flight);
                if (price === null) return;

                const boundsCount = flight.bounds?.length || 0;
                for (let i = 0; i < boundsCount; i++) {
                    const stops = getFlightStops(flight, i);
                    const key = `bound${i}-${stops}`;
                    update(key, price);
                }
            });

        } else {
            // One-way
            allFlights.forEach(flight => {
                const stops = getFlightStops(flight);
                const price = getPrice(flight);
                if (price === null) return;
                update(stops.toString(), price);
            });
        }

        return best;
    };

    const bestPricePerStop = getBestPricePerStop();

    // Calculate best price per airline
    const getBestPricePerAirline = () => {
        if (!allFlights || allFlights.length === 0) {
            return {};
        }

        const bestPricePerAirline = {};

        allFlights.forEach(flight => {
            // Get airline code from first bound's first airline
            const airlineCode = flight?.bounds?.[0]?.airlines?.[0];
            if (!airlineCode) return;

            const price = getPrice(flight);
            if (price === null) return;

            if (!bestPricePerAirline[airlineCode] || price < bestPricePerAirline[airlineCode]) {
                bestPricePerAirline[airlineCode] = price;
            }
        });

        return bestPricePerAirline;
    };

    const bestPricePerAirline = getBestPricePerAirline();

    // Calculate current best price based on selected filters
    const getCurrentBestPrice = () => {
        if (!allFlights || allFlights.length === 0) return null;

        const tripType = getTripTypeFromFlight(allFlights[0]);
        const isRoundTrip = tripType === 'rt';
        const isMultiDest = tripType === 'md';

        const { onwardStops, returnStops } = filters;

        const anyOnwardSelected = onwardStops.direct || onwardStops.oneStop || onwardStops.twoStop;
        const anyReturnSelected = returnStops.direct || returnStops.oneStop || returnStops.twoStop;

        let minPrice = null;

        if (isRoundTrip) {
            // Round-trip: check selected stop combinations
            const onwardStopsSelected = [
                onwardStops.direct ? 0 : null,
                onwardStops.oneStop ? 1 : null,
                onwardStops.twoStop ? 2 : null,
            ].filter(n => n !== null);

            const returnStopsSelected = [
                returnStops.direct ? 0 : null,
                returnStops.oneStop ? 1 : null,
                returnStops.twoStop ? 2 : null,
            ].filter(n => n !== null);

            if (!anyOnwardSelected && !anyReturnSelected) return null;

            allFlights.forEach(flight => {
                const depStops = getFlightStops(flight, 'departure');
                const retStops = getFlightStops(flight, 'return');
                const price = getPrice(flight);
                if (price === null) return;

                const onwardMatch = !anyOnwardSelected || onwardStopsSelected.includes(depStops);
                const returnMatch = !anyReturnSelected || returnStopsSelected.includes(retStops);

                if (onwardMatch && returnMatch) {
                    if (minPrice === null || price < minPrice) {
                        minPrice = price;
                    }
                }
            });
        } else if (isMultiDest) {
            // Multi-destination: check all bounds dynamically

            if (!filters.multiDestStops) return null;

            let hasAnyFilter = false;
            Object.values(filters.multiDestStops).forEach(b => {
                if (b?.direct || b?.oneStop || b?.twoStop) {
                    hasAnyFilter = true;
                }
            });

            if (!hasAnyFilter) return null;

            allFlights.forEach(flight => {
                const price = getPrice(flight);
                if (price === null) return;

                const boundsCount = flight.bounds?.length || 0;
                let matchesAllBounds = true;

                for (let i = 0; i < boundsCount; i++) {
                    const boundFilter = filters.multiDestStops?.[i];
                    if (!boundFilter) continue;

                    const selectedStops = [
                        boundFilter.direct ? 0 : null,
                        boundFilter.oneStop ? 1 : null,
                        boundFilter.twoStop ? 2 : null,
                    ].filter(n => n !== null);

                    if (selectedStops.length === 0) continue;

                    const stops = getFlightStops(flight, i);

                    if (!selectedStops.includes(stops)) {
                        matchesAllBounds = false;
                        break;
                    }
                }

                if (matchesAllBounds) {
                    if (minPrice === null || price < minPrice) {
                        minPrice = price;
                    }
                }
            });
        } else {
            // One-way
            const selectedStops = [
                onwardStops.direct ? 0 : null,
                onwardStops.oneStop ? 1 : null,
                onwardStops.twoStop ? 2 : null,
            ].filter(n => n !== null);

            if (!anyOnwardSelected) return null;

            allFlights.forEach(flight => {
                const stops = getFlightStops(flight);
                const price = getPrice(flight);
                if (price === null) return;

                if (selectedStops.includes(stops)) {
                    if (minPrice === null || price < minPrice) {
                        minPrice = price;
                    }
                }
            });
        }

        return minPrice;
    };

    const currentBestPrice = getCurrentBestPrice();

    // Format price for display
    const formatPrice = (price) => {
        if (price === null || price === undefined || isNaN(price)) return '';
        return price.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
    };
    const airlineList = useMemo(() => {
        const sourceFlights =
            allFlights ?? availabilityResults?.allFlightCombinations ?? [];
        const availableCodes = new Set();

        sourceFlights.forEach((flight) => {
            const airline =
                flight?.validatingCarrier ||
                flight?.airline ||
                flight?.bounds?.[0]?.airlines?.[0];
            if (airline) availableCodes.add(airline);
        });

        return Object.values(airlineDictionary).filter((airline) =>
            availableCodes.has(airline.code)
        );
    }, [allFlights, availabilityResults?.allFlightCombinations, airlineDictionary]);
    let [minPrice, maxPrice] = getMinMaxPrice(allFlights);

    const tripType = getTripTypeFromFlight(allFlights?.[0]);
    const isRoundTrip = tripType === 'rt';
    const isMultiDest = tripType === 'md';

    const clearFilters = () => {
        setFilters({
            selectedAirlines: [],
            sortBy: {
                lowPrice: true,
                highPrice: false,
                fastest: false,
                slowest: false,
                smartest: false,
            },
            priceRange: [minPrice, maxPrice],
            priceFilterActive: false,
            onwardStops: {
                direct: false,
                oneStop: false,
                twoStop: false,
            },
            returnStops: {
                direct: false,
                oneStop: false,
                twoStop: false,
            },
            multiDestStops: {},
            departureAirports: [],
            arrivalAirports: [],
            departureTimeSlots: {},
        });
    };

    const handleAirline = (code, isChecked) => {
        setFilters((prev) => {
            const airlines = [...prev.selectedAirlines];
            if (isChecked) {
                if (!airlines.includes(code)) {
                    airlines.push(code);
                }
            } else {
                const index = airlines.indexOf(code);
                if (index > -1) {
                    airlines.splice(index, 1);
                }
            }
            return {
                ...prev,
                selectedAirlines: airlines,
            };
        });
    };

    const handleOnwardStop = (type, isChecked) => {
        setFilters((prev) => ({
            ...prev,
            onwardStops: {
                ...prev.onwardStops,
                [type]: isChecked,
            },
        }));
    };

    const handleReturnStop = (type, isChecked) => {
        setFilters((prev) => ({
            ...prev,
            returnStops: {
                ...prev.returnStops,
                [type]: isChecked,
            },
        }));
    };

    const handleMultiDestStop = (boundIndex, type, isChecked) => {
        setFilters((prev) => ({
            ...prev,
            multiDestStops: {
                ...prev.multiDestStops,
                [boundIndex]: {
                    ...prev.multiDestStops?.[boundIndex],
                    [type]: isChecked,
                },
            },
        }));
    };
    return (
        <div className="w-full rounded-xl p-2 flex flex-col gap-2 border border-border-color">
            <Card className="card-body rounded-md p-4">
                <div className="mb-2 text-default fw-bold text-md">
                    {t("orderBy")}
                </div>
                <Col>
                    {(() => {
                        const sortOptions = [
                            { value: "lowPrice", label: t("lowPrice"), id: 1 },
                            {
                                 value: "highPrice",
                                 label: t("highPrice"),
                                 id: 2,
                                },
                            { value: "fastest", label: t("fastest"), id: 3 },
                            { value: "slowest", label: t("slowest"), id: 4 },
                            { value: "smartest", label: t("smartest"), id: 5 },
                        ];

                        const selectedSortOption =
                            sortOptions.find((opt) => filters.sortBy?.[opt.value]) ||
                            sortOptions[0];

                        return (
                    <SimpleSelect
                        options={sortOptions}
                        onChange={(selectedOpt) => {
                            setFilters((prev) => ({
                                ...prev,
                                sortBy: {
                                    lowPrice: false,
                                    highPrice: false,
                                    fastest: false,
                                    slowest: false,
                                    smartest: false,
                                    [selectedOpt.value]: true,
                                },
                            }));
                        }}
                        placeholder={t("select")}
                        value={""}
                        selectedOption={selectedSortOption}
                    />
                        );
                    })()}
                </Col>
            </Card>
            <Card className="card-body rounded-md p-4">
                <div className="mb-2 text-default fw-bold text-md">
                    {t("price")}
                </div>
                <div className="position-relative">
                    <div className="noui-wrapper">
                        <div className="d-flex justify-content-between">
                            <input
                                type="text"
                                className="text-body input-with-range-min"
                                value={filters.priceRange[0].toFixed(0)}
                                readOnly
                            />
                            <input
                                type="text"
                                className="text-body input-with-range-max"
                                value={filters.priceRange[1].toFixed(0)}
                                readOnly
                            />
                        </div>
                        <Nouislider
                            start={filters.priceRange}
                            range={{ min: minPrice, max: maxPrice }}
                            step={1}
                            onChange={(values) => {
                                setFilters((prev) => ({
                                    ...prev,
                                    priceRange: values.map(v => parseFloat(v)),
                                    priceFilterActive: true,
                                }));
                            }}
                            className="noui-slider-range mt-2 b-none"
                            connect
                        />
                    </div>
                </div>
            </Card>
            <Card className="card-body rounded-md p-4">
                <div className="left_side_search_heading">
                    <div className="text-md text-default fw-bold">
                        {t("firstDestination")}
                    </div>
                </div>
                <div className="tour_search_type">
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="onwardDirect"
                            checked={filters.onwardStops.direct}
                            onChange={(e) =>
                                handleOnwardStop("direct", e.target.checked)
                            }
                        />
                        <label
                            className="form-check-label d-flex justify-content-between w-100"
                            htmlFor="onwardDirect"
                        >
                            <span className="area_flex_one">{t("direct")}</span>
                            <small className="text-black">{formatPrice(bestPricePerStop[isRoundTrip ? 'dep-0' : isMultiDest ? 'bound0-0' : '0'])}</small>
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="onwardOneStop"
                            checked={filters.onwardStops.oneStop}
                            onChange={(e) =>
                                handleOnwardStop("oneStop", e.target.checked)
                            }
                        />
                        <label
                            className="form-check-label d-flex justify-content-between w-100"
                            htmlFor="onwardOneStop"
                        >
                            <span className="area_flex_one">{t("stop1")}</span>
                            <small className="text-black">{formatPrice(bestPricePerStop[isRoundTrip ? 'dep-1' : isMultiDest ? 'bound0-1' : '1'])}</small>
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="onwardTwoStop"
                            checked={filters.onwardStops.twoStop}
                            onChange={(e) =>
                                handleOnwardStop("twoStop", e.target.checked)
                            }
                        />
                        <label
                            className="form-check-label d-flex justify-content-between w-100"
                            htmlFor="onwardTwoStop"
                        >
                            <span className="area_flex_one">{t("stop+2")}</span>
                            <small className="text-black">{formatPrice(bestPricePerStop[isRoundTrip ? 'dep-2' : isMultiDest ? 'bound0-2' : '2'])}</small>
                        </label>
                    </div>
                </div>
            </Card>
            {isRoundTrip && (
                <Card className="card-body rounded-md p-4">
                    <div className="left_side_search_heading">
                        <div className="text-md text-default fw-bold">
                            {t("returnStops")}
                        </div>
                    </div>
                    <div className="tour_search_type">
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="returnDirect"
                                checked={filters.returnStops.direct}
                                onChange={(e) =>
                                    handleReturnStop("direct", e.target.checked)
                                }
                            />
                            <label
                                className="form-check-label d-flex justify-content-between w-100"
                                htmlFor="returnDirect"
                            >
                                <span className="area_flex_one">
                                    {t("direct")}
                                </span>
                                <small className="text-black">{formatPrice(bestPricePerStop['ret-0'])}</small>
                            </label>
                        </div>
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="returnOneStop"
                                checked={filters.returnStops.oneStop}
                                onChange={(e) =>
                                    handleReturnStop(
                                        "oneStop",
                                        e.target.checked
                                    )
                                }
                            />
                            <label
                                className="form-check-label d-flex justify-content-between w-100"
                                htmlFor="returnOneStop"
                            >
                                <span className="area_flex_one">
                                    {t("stop1")}
                                </span>
                                <small className="text-black">{formatPrice(bestPricePerStop['ret-1'])}</small>
                            </label>
                        </div>
                        <div className="form-check">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="returnTwoStop"
                                checked={filters.returnStops.twoStop}
                                onChange={(e) =>
                                    handleReturnStop(
                                        "twoStop",
                                        e.target.checked
                                    )
                                }
                            />
                            <label
                                className="form-check-label d-flex justify-content-between w-100"
                                htmlFor="returnTwoStop"
                            >
                                <span className="area_flex_one">
                                    {t("stop+2")}
                                </span>
                                <small className="text-black">{formatPrice(bestPricePerStop['ret-2'])}</small>
                            </label>
                        </div>
                    </div>
                </Card>
            )}
            {/* Multi-destination: Additional stop filters for each bound after the first */}
            {isMultiDest && allFlights?.[0]?.bounds?.slice(1).map((_bound, boundIndex) => {
                const actualBoundIndex = boundIndex + 1;
                const boundKey0 = `bound${actualBoundIndex}-0`;
                const boundKey1 = `bound${actualBoundIndex}-1`;
                const boundKey2 = `bound${actualBoundIndex}-2`;
                const hasAnyPrice = bestPricePerStop[boundKey0] != null || bestPricePerStop[boundKey1] != null || bestPricePerStop[boundKey2] != null;
                if (!hasAnyPrice) return null;
                return (
                    <Card key={actualBoundIndex} className="card-body rounded-md p-4">
                        <div className="left_side_search_heading">
                            <div className="text-md text-default fw-bold">
                                {actualBoundIndex === 1 ? t("secondDestination") : t("thirdDestination")}
                            </div>
                        </div>
                        <div className="tour_search_type">
                            {bestPricePerStop[boundKey0] != null && (
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`md-${actualBoundIndex}-direct`}
                                        checked={filters.multiDestStops?.[actualBoundIndex]?.direct || false}
                                        onChange={(e) =>
                                            handleMultiDestStop(actualBoundIndex, "direct", e.target.checked)
                                        }
                                    />
                                    <label
                                        className="form-check-label d-flex justify-content-between w-100"
                                        htmlFor={`md-${actualBoundIndex}-direct`}
                                    >
                                        <span className="area_flex_one">{t("direct")}</span>
                                        <small className="text-black">{formatPrice(bestPricePerStop[boundKey0])}</small>
                                    </label>
                                </div>
                            )}
                            {bestPricePerStop[boundKey1] != null && (
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`md-${actualBoundIndex}-oneStop`}
                                        checked={filters.multiDestStops?.[actualBoundIndex]?.oneStop || false}
                                        onChange={(e) =>
                                            handleMultiDestStop(actualBoundIndex, "oneStop", e.target.checked)
                                        }
                                    />
                                    <label
                                        className="form-check-label d-flex justify-content-between w-100"
                                        htmlFor={`md-${actualBoundIndex}-oneStop`}
                                    >
                                        <span className="area_flex_one">{t("stop1")}</span>
                                        <small className="text-black">{formatPrice(bestPricePerStop[boundKey1])}</small>
                                    </label>
                                </div>
                            )}
                            {bestPricePerStop[boundKey2] != null && (
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`md-${actualBoundIndex}-twoStop`}
                                        checked={filters.multiDestStops?.[actualBoundIndex]?.twoStop || false}
                                        onChange={(e) =>
                                            handleMultiDestStop(actualBoundIndex, "twoStop", e.target.checked)
                                        }
                                    />
                                    <label
                                        className="form-check-label d-flex justify-content-between w-100"
                                        htmlFor={`md-${actualBoundIndex}-twoStop`}
                                    >
                                        <span className="area_flex_one">{t("stop+2")}</span>
                                        <small className="text-black">{formatPrice(bestPricePerStop[boundKey2])}</small>
                                    </label>
                                </div>
                            )}
                        </div>
                    </Card>
                );
            })}
                {!isMultiDest && currentBestPrice != null && (
                    <Card className="card-body rounded-md p-4">
                        <div className="mb-2">
                            <small className="text-blue fw-bold">
                                {t("BEST_PRICE_FOR_SELECTED_STOPS")}: {formatPrice(currentBestPrice)}
                            </small>
                        </div>
                    </Card>
                )}
            <Card className="card-body rounded-md p-4">
                <div className="mb-2 text-default fw-bold text-md">
                    {t("preferredAirline")}
                </div>
                <Col xs={12}>
                    {airlineList &&
                        airlineList.slice(0, 4).map((value, index) => (
                            <div
                                key={index}
                                className="justify-content-start gap-2 align-items-center d-flex"
                            >
                                <input
                                    onChange={(e) =>
                                        handleAirline(
                                            value.code,
                                            e.target.checked
                                        )
                                    }
                                    className="form-check-input mt-0"
                                    type="checkbox"
                                    id={value.code}
                                    checked={filters.selectedAirlines.includes(
                                        value.code
                                    )}
                                />
                                <label
                                    className="d-flex justify-content-between align-items-center w-100"
                                    htmlFor={value.code}
                                >
                                    <div className="d-flex gap-2 align-items-center">
                                        <Image
                                            src={`${process.env.REACT_APP_STATIC_URL}/${urls.flight.airlines}/${value.code}.png`}
                                            style={{ height: "30px" }}
                                        />
                                        {value.name}
                                    </div>
                                    <small className="text-black">{formatPrice(bestPricePerAirline[value.code])}</small>
                                </label>
                            </div>
                        ))}

                    <Collapse in={isOpen}>
                        <div>
                            {airlineList &&
                                airlineList.slice(4).map((value, index) => (
                                    <div
                                        key={index}
                                        className="justify-content-start gap-2 align-items-center d-flex"
                                    >
                                        <input
                                            onChange={(e) =>
                                                handleAirline(
                                                    value.code,
                                                    e.target.checked
                                                )
                                            }
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`${value.code}-more`}
                                            checked={filters.selectedAirlines.includes(
                                                value.code
                                            )}
                                        />
                                        <label
                                            className="d-flex justify-content-between align-items-center w-100"
                                            htmlFor={`${value.code}-more`}
                                        >
                                            <div className="d-flex gap-2">
                                                <Image
                                                    src={`${process.env.REACT_APP_STATIC_URL}/${urls.flight.airlines}/${value.code}.png`}
                                                    style={{ height: "30px" }}
                                                />
                                                {value.name}
                                            </div>
                                            <small className="text-black">{formatPrice(bestPricePerAirline[value.code])}</small>
                                        </label>
                                    </div>
                                ))}
                        </div>
                    </Collapse>

                    <button
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                        className="p-0 mb-0 mt-2 btn-more d-flex align-items-center collapsed"
                    >
                        {!isOpen ? (
                            <Fragment>
                                <div className="gap-2 d-flex align-items-center">
                                    <span className="see-more">
                                        {t("seeMore")}
                                    </span>
                                    <FaAngleDown className="ms-2" />
                                </div>
                            </Fragment>
                        ) : (
                            <Fragment>
                                <div className="gap-2 d-flex align-items-center">
                                    <span>{t("seeLess")}</span>
                                    <FaAngleUp className="ms-2" />
                                </div>
                            </Fragment>
                        )}
                    </button>
                </Col>
            </Card>
            {/* Departure time slots */}
            {(() => {
                if (!allFlights?.[0]?.bounds?.length) return null;
                const tripType = getTripTypeFromFlight(allFlights[0]);
                const boundsCount = allFlights[0].bounds.length;
                const currentBound = Math.min(activeTimeTab, boundsCount - 1);
                const depLocationId = allFlights[0].bounds[currentBound]?.departure?.locationId;
                const airportName = airports[depLocationId]?.display || depLocationId || '';
                const tabLabels = tripType === 'rt'
                    ? [t('departure'), t('return')]
                    : boundsCount > 1
                        ? allFlights[0].bounds.map((_, i) => `${t('flight')} ${i + 1}`)
                        : null;
                const timeSlots = [
                    { key: 'earlyMorning', emoji: '🌙', label: t('earlyMorning'), range: '00:00 – 07:59' },
                    { key: 'morning',      emoji: '☀️', label: t('morning'),      range: '08:00 – 15:59' },
                    { key: 'evening',      emoji: '⛅', label: t('evening'),       range: '16:00 – 23:59' },
                ];
                return (
                    <Card className="card-body rounded-md p-4">
                        {tabLabels && (
                            <div className="d-flex gap-1 mb-3">
                                {tabLabels.map((label, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveTimeTab(i)}
                                        className={`flex-1 py-1 rounded border fw-semibold`}
                                        style={{
                                            fontSize: '0.78rem',
                                            borderColor: activeTimeTab === i ? 'var(--bs-primary, #0d6efd)' : '#dee2e6',
                                            background: activeTimeTab === i ? 'rgba(13,110,253,0.08)' : '#f8f9fa',
                                            color: activeTimeTab === i ? 'var(--bs-primary, #0d6efd)' : '#212529',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                        {airportName && (
                            <div className="text-muted mb-2" style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                {airportName}
                            </div>
                        )}
                        <div className="tour_search_type d-flex flex-column gap-2">
                            {timeSlots.map(({ key, emoji, label, range }) => {
                                const checked = filters.departureTimeSlots?.[currentBound]?.[key] || false;
                                return (
                                    <div key={key} className="form-check d-flex align-items-center gap-2 m-0">
                                        <input
                                            className="form-check-input mt-0 flex-shrink-0"
                                            type="checkbox"
                                            id={`ts-${currentBound}-${key}`}
                                            checked={checked}
                                            onChange={() => setFilters(prev => ({
                                                ...prev,
                                                departureTimeSlots: {
                                                    ...prev.departureTimeSlots,
                                                    [currentBound]: {
                                                        ...prev.departureTimeSlots?.[currentBound],
                                                        [key]: !checked,
                                                    },
                                                },
                                            }))}
                                        />
                                        <label
                                            className="form-check-label d-flex align-items-center justify-content-between w-100"
                                            htmlFor={`ts-${currentBound}-${key}`}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <span className="d-flex align-items-center gap-2">
                                                <span>{emoji}</span>
                                                <span className="area_flex_one">{label}</span>
                                            </span>
                                            <small className="text-muted">({range})</small>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                );
            })()}
            <button
                onClick={clearFilters}
                className="btn btn-primary bg-main-color mt-2"
            >
                {t("clearFilters")}
            </button>
        </div>
    );
};

export default SideBar;
